import React from "react";
import { URL_RE, splitUrl, shortUrlLabel } from "@/lib/linkText";
import { findInlineMatch } from "@/lib/richFormat";

// Rendu React de la mise en forme légère des publications (voir lib/richFormat.js pour la saisie).
// Blocs : « > » citation, « - » liste à puces, sinon paragraphes. En ligne : **gras**, *italique*, __souligné__.
// Aucune injection HTML (pas de dangerouslySetInnerHTML) → pas de risque XSS. Le texte des feuilles est
// délégué à `renderLeaf` (mentions @ / #hashtags) pour préserver le comportement existant ; les URL
// deviennent des liens cliquables (libellé court, ouverture externe) avant d'atteindre `renderLeaf`.

const linkStyle = { color: "var(--yalola-primary)", overflowWrap: "anywhere" };

export function renderLinks(text, renderLeaf, key) {
  const out = [];
  const re = new RegExp(URL_RE.source, "gi");
  let last = 0;
  let k = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const { url } = splitUrl(m[0]);
    if (!url) continue;
    if (m.index > last) out.push(<React.Fragment key={`${key}-l${k++}`}>{renderLeaf(text.slice(last, m.index))}</React.Fragment>);
    out.push(
      <a key={`${key}-a${k++}`} href={url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} className="font-semibold hover:underline" style={linkStyle} data-testid="text-link">
        {shortUrlLabel(url)}
      </a>
    );
    last = m.index + url.length;
  }
  if (!out.length) return renderLeaf(text);
  if (last < text.length) out.push(<React.Fragment key={`${key}-l${k++}`}>{renderLeaf(text.slice(last))}</React.Fragment>);
  return out;
}

const TAGS = { bold: "strong", underline: "u", italic: "em" };
const findMatch = findInlineMatch;

function parseInline(text, renderLeaf, key) {
  if (!text) return null;
  const nodes = [];
  let remaining = text;
  let i = 0;
  while (remaining) {
    const m = findMatch(remaining);
    if (!m) {
      nodes.push(<React.Fragment key={`${key}-t${i++}`}>{renderLinks(remaining, renderLeaf, `${key}-u${i}`)}</React.Fragment>);
      break;
    }
    if (m.start > 0) {
      const lead = remaining.slice(0, m.start);
      nodes.push(<React.Fragment key={`${key}-t${i++}`}>{renderLinks(lead, renderLeaf, `${key}-u${i}`)}</React.Fragment>);
    }
    const inner = remaining.slice(m.start + m.rule.open.length, m.close);
    const Tag = TAGS[m.rule.kind];
    nodes.push(<Tag key={`${key}-f${i++}`}>{parseInline(inner, renderLeaf, `${key}-n${i}`)}</Tag>);
    remaining = remaining.slice(m.close + m.rule.close.length);
  }
  return nodes;
}

const isQuote = (l) => /^\s*>\s?/.test(l);
const isBullet = (l) => /^\s*-\s+/.test(l);

// Rend `text` (avec repères de mise en forme) en blocs React. `renderLeaf(str)` gère les mentions/hashtags.
export function renderRichText(text, renderLeaf = (s) => s) {
  const src = String(text || "");
  if (!src) return null;
  const lines = src.split("\n");
  const blocks = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    if (isQuote(lines[i])) {
      const q = [];
      while (i < lines.length && isQuote(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, "")); i++; }
      blocks.push(
        <blockquote key={`b${k++}`} className="border-l-[3px] pl-3 my-1.5 italic opacity-85" style={{ borderColor: "var(--yalola-primary)" }}>
          {q.map((line, li) => (
            <React.Fragment key={li}>{parseInline(line, renderLeaf, `q${k}-${li}`)}{li < q.length - 1 ? <br /> : null}</React.Fragment>
          ))}
        </blockquote>
      );
    } else if (isBullet(lines[i])) {
      const items = [];
      while (i < lines.length && isBullet(lines[i])) { items.push(lines[i].replace(/^\s*-\s+/, "")); i++; }
      blocks.push(
        <ul key={`b${k++}`} className="list-disc pl-5 my-1.5 space-y-0.5">
          {items.map((it, ii) => <li key={ii}>{parseInline(it, renderLeaf, `l${k}-${ii}`)}</li>)}
        </ul>
      );
    } else {
      const para = [];
      while (i < lines.length && !isQuote(lines[i]) && !isBullet(lines[i])) { para.push(lines[i]); i++; }
      blocks.push(
        <span key={`b${k++}`} className="block">
          {para.map((line, pi) => (
            <React.Fragment key={pi}>{parseInline(line, renderLeaf, `p${k}-${pi}`)}{pi < para.length - 1 ? <br /> : null}</React.Fragment>
          ))}
        </span>
      );
    }
  }
  return blocks;
}

export default renderRichText;
